/**
 * useExperts Hook
 *
 * FERP Sprint 5 - Professional Features
 *
 * React hook for accessing expert directory via GraphQL repository pattern.
 *
 * Architecture Compliance:
 * - ✅ Repository pattern (wraps GraphQL query)
 * - ✅ Domain-agnostic (all data from backend)
 * - ✅ Life-critical safety (error handling)
 * - ✅ Multi-tenant security (tenant from context)
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCurrentTenant } from './useCurrentTenant';

/**
 * GraphQL query for expert directory
 */
const EXPERTS_QUERY = gql`
  query Experts($input: ExpertsInput!) {
    experts(input: $input) {
      experts {
        id
        tenant
        name
        bio
        specialty
        hourlyRate
        currency
        availability
        rating
        reviewCount
        timezone
        languages
        certifications
        createdAt
      }
      totalCount
    }
  }
`;

/**
 * Expert profile interface
 */
export interface Expert {
  id: string;
  tenant: string;
  name: string;
  bio: string;
  specialty: string;
  hourlyRate: number;
  currency: string;
  availability: string;
  rating: number;
  reviewCount: number;
  timezone: string;
  languages: string[];
  certifications: string[];
  createdAt: string;
}

/**
 * Hook options interface
 */
export interface UseExpertsOptions {
  /**
   * Filter by specialty
   */
  specialty?: string;

  /**
   * Minimum rating filter (e.g., 4.5)
   */
  minRating?: number;

  /**
   * Filter by availability
   */
  availableOnly?: boolean;
}

/**
 * Hook result interface
 */
export interface UseExpertsResult {
  /**
   * List of experts matching filters
   */
  experts: Expert[];

  /**
   * Total count of experts
   */
  totalCount: number;

  /**
   * Loading state
   */
  loading: boolean;

  /**
   * Error state
   */
  error: Error | undefined;

  /**
   * Refetch function
   */
  refetch: () => Promise<any>;
}

/**
 * useExperts Hook
 *
 * Queries expert directory from backend via GraphQL.
 * Supports filtering by specialty, rating, and availability.
 *
 * @param options - Filter options
 * @returns Expert directory data and query state
 *
 * @example
 * ```tsx
 * function ExpertDirectory() {
 *   const { experts, loading, error } = useExperts({
 *     specialty: 'tax_accounting',
 *     minRating: 4.5,
 *     availableOnly: true
 *   });
 *
 *   if (loading) return <Loader />;
 *   if (error) return <Alert color="red">{error.message}</Alert>;
 *
 *   return (
 *     <div>
 *       {experts.map(expert => (
 *         <ExpertCard key={expert.id} expert={expert} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useExperts(
  options: UseExpertsOptions = {}
): UseExpertsResult {
  // ✅ FERP-Compliant: Get tenant from context (not hardcoded)
  const { tenantId } = useCurrentTenant();

  // Query GraphQL API with filters
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(EXPERTS_QUERY, {
    variables: {
      input: {
        tenant: tenantId,
        specialty: options.specialty || null,
        minRating: options.minRating || null,
        availableOnly: options.availableOnly || false,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !tenantId, // Don't query without tenant
  });

  return {
    experts: data?.experts?.experts || [],
    totalCount: data?.experts?.totalCount || 0,
    loading,
    error,
    refetch,
  };
}
